import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="address"
export default class extends Controller {
  static targets = [
    "street",
    "street_other",
    "city",
    "state",
    "zip",
    "country",
    "flag",
  ];

  static googleMapsLoaded = false;

  connect() {
    if (!window.google || !window.google.maps) {
      this.loadGoogleMaps();
      this.boundInitMaps = this.initMaps.bind(this);
      window.addEventListener("map:init", this.boundInitMaps, {
        once: true,
      });
    } else {
      this.initMaps();
    }
  }

  loadGoogleMaps() {
    if (window._googleMapsLoading) return;
    window._googleMapsLoading = true;

    window.initMap = function () {
      const event = new Event("map:init", { bubbles: true, cancelable: false });
      window.dispatchEvent(event);
    };

    try {
      // Find the API key from the closest ancestor with data-gmaps-key
      let el = this.element;
      let key = null;
      while (el && !key) {
        key = el.getAttribute && el.getAttribute("data-gmaps-key");
        el = el.parentElement;
      }
      if (!key) {
        const error = new Error("Google Maps API key not found in data-gmaps-key attribute");
        console.error("[address]", error, {
          controller: "address",
          action: "loadGoogleMaps",
        });
        console.error(
          "Google Maps API key not found in data-gmaps-key attribute.",
        );
        return;
      }

      const src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&callback=initMap`;
      if (!Array.from(document.scripts).some((s) => s.src === src)) {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
          const error = new Error("Failed to load Google Maps API");
          console.error("[address]", error, {
            controller: "address",
            action: "loadGoogleMaps",
          });
        };

        document.head.appendChild(script);
      }
    } catch (error) {
      console.error("[address]", error, {
        controller: "address",
        action: "loadGoogleMaps",
      });
    }
  }

  initMaps() {
    try {
      if (!this.hasStreetTarget) return;
      this.boundFocusHandler = (event) => {
        event.target.setAttribute("autocomplete", "new-password");
      };
      this.streetTarget.addEventListener("focus", this.boundFocusHandler);
      this.autocomplete = new google.maps.places.Autocomplete(this.streetTarget, {
        fields: ["address_components"],
        types: ["address"],
      });
      this.boundPlaceSelected = this.placeSelected.bind(this);
      this.placeChangedListener = this.autocomplete.addListener(
        "place_changed",
        this.boundPlaceSelected,
      );
      this.mapInit = true;
    } catch (error) {
      console.error("[address]", error, {
        controller: "address",
        action: "initMaps",
      });
    }
  }

  placeSelected() {
    try {
      const place = this.autocomplete.getPlace();

      if (!place || !place.address_components) {
        return;
      }

      // Google Maps API guarantees address_components is an array if present
      let address1 = "";
      let postcode = "";
      for (const component of place.address_components) {
        if (!component.types || component.types.length === 0) continue;
        const componentType = component.types[0];

        switch (componentType) {
          case "street_number": {
            address1 = `${component.long_name} ${address1}`;
            break;
          }
          case "route": {
            address1 += component.short_name;
            break;
          }
          case "subpremise": {
            if (this.hasStreet_otherTarget) {
              this.street_otherTarget.value = `#${component.long_name.toUpperCase()}`;
            }
            break;
          }
          case "postal_code": {
            postcode = `${component.long_name}${postcode}`;
            break;
          }
          case "postal_code_suffix": {
            postcode = `${postcode}-${component.long_name}`;
            break;
          }
          case "locality":
            if (this.hasCityTarget) this.cityTarget.value = component.long_name;
            break;
          case "administrative_area_level_1": {
            if (this.hasStateTarget) {
              this.stateTarget.value = component.short_name;
              this.stateTarget.dispatchEvent(
                new Event("change", { bubbles: true }),
              );
            }
            break;
          }
          case "country":
            if (this.hasCountryTarget)
              this.countryTarget.value =
                component.short_name === "US" ? "US" : component.long_name;
            break;
        }
      }
      if (this.hasStreetTarget) this.streetTarget.value = address1;
      if (this.hasZipTarget) this.zipTarget.value = postcode;
    } catch (error) {
      console.error("[address]", error, {
        controller: "address",
        action: "placeSelected",
      });
      console.warn("Failed to parse address from Google Maps:", error);
    }
  }

  disconnect() {
    if (this.boundInitMaps) {
      window.removeEventListener("map:init", this.boundInitMaps);
    }
    if (this.hasStreetTarget && this.boundFocusHandler) {
      this.streetTarget.removeEventListener("focus", this.boundFocusHandler);
    }
    if (this.placeChangedListener) {
      google.maps.event.removeListener(this.placeChangedListener);
    }
  }
}

